/**
 * Everything the reconciliation screens ask the server for, in one file.
 *
 * Every call here reaches a real endpoint now. Each one names the route it uses
 * and, where the route cannot do the whole job, says what it cannot do rather
 * than quietly doing less — see `settle`, which has no settlements endpoint to
 * call and applies each bill on its own instead.
 */

import { apiCall, API_BASE_URL } from '../../config/api';
import { parseBankStatementFile } from './bankStatementParser';

/* The creditor table calls its GST column `gst`; the form has always called it
   `gstNo`. Translate at the edge so neither side has to change. */
const fromApi = (c) => ({
  id: c.id, name: c.name || '', address: c.address || '',
  gstNo: c.gst || '', pan: c.pan || '', tan: c.tan || '', cin: c.cin || '',
  // What we have paid them that is not yet against a bill of theirs.
  balance: Number(c.debit) || 0,
});
const toApi = (f) => ({
  name: f.name, address: f.address || null,
  gst: f.gstNo || null, pan: f.pan || null, tan: f.tan || null, cin: f.cin || null,
});

/* `apiCall` hands back the raw Response, the way every other caller in this
   app uses it, so the body is read here — and a refusal is raised with what
   the server actually said rather than a generic failure. */
const readJson = async (response) => {
  let body = {};
  try { body = await response.json(); } catch { /* an error page need not be JSON */ }
  if (!response.ok || body.success === false) {
    throw new Error(body.message || `Request failed (${response.status})`);
  }
  return body;
};

const wait = (ms = 420) => new Promise((r) => setTimeout(r, ms));

/* ── money ───────────────────────────────────────────────────────────────── */

const inr = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inr0 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

/** `12345.6` → `12,345.60`. Paise matter here — a PUR total carried .20 of them. */
export const money = (n) => inr.format(Number(n) || 0);
/**
 * `7640000` → `76.40 L`, `12345678` → `1.23 Cr`.
 *
 * Lakh and crore, the way the figure is said here, for the cards at the top:
 * a seven-figure amount written out in full is read digit by digit before it
 * means anything. Under a lakh there is nothing to shorten, so it is printed
 * whole, without the paise. The exact amount is always a hover away.
 */
export const moneyShort = (n) => {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  if (abs >= 1e7) return `${(v / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `${(v / 1e5).toFixed(2)} L`;
  return inr0.format(v);
};

/**
 * `2026-11-08` → `08/11/2026`.
 *
 * The API speaks ISO and so does every sort in this file, which is why the
 * value is kept as it comes and turned round only where it is read.
 */
export const dmy = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso || '');
};

/** Two amounts are "the same" when they differ by less than a paisa. */
export const sameMoney = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) < 0.01;

/* ── fixtures ──────────────────────────────────────────────────────────────
   Only the calls that have no endpoint yet read from these. */

/* ── creditors (step 1) ──────────────────────────────────────────────────── */

/** GET /api/invoices/creditors */
export async function listCreditors() {
  const body = await readJson(await apiCall('/invoices/creditors', { method: 'GET' }));
  return (body.data || []).map(fromApi);
}

/** POST /api/invoices/creditors */
export async function createCreditor(body) {
  const res = await apiCall('/invoices/creditors', {
    method: 'POST',
    body: JSON.stringify(toApi(body)),
  });
  return fromApi((await readJson(res)).data || {});
}

/** PATCH /api/invoices/creditors/:id */
export async function updateCreditor(id, body) {
  const res = await apiCall(`/invoices/creditors/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(toApi(body)),
  });
  return fromApi((await readJson(res)).data || {});
}

/** DELETE /api/invoices/creditors/:id */
export async function deleteCreditor(id) {
  await readJson(await apiCall(`/invoices/creditors/${id}`, { method: 'DELETE' }));
  return { ok: true };
}

/* ── PUR extraction ──────────────────────────────────────────────────────── */

/**
 * POST /api/scraper/extract-pur  (multipart)
 *
 * The bill is read against the creditor that sent it and saved as a document
 * with its line items, filed under the shipment it was uploaded for — the
 * server looks a PUR up by that number as well as by the AWBs printed on its
 * own rows. Sent as FormData, so this one cannot go through
 * `apiCall` — that always sets a JSON content type, and the browser has to set
 * the multipart boundary itself.
 *
 * Returns { ok, duplicate, extractionStatus, invoiceNumber, lines, warnings,
 * message }. Note `extractionStatus` — a PUR's `status` is how much of it has
 * been paid, which is a different thing entirely.
 * The caller treats a failure as "the PDF is filed, the read is not" rather
 * than as a failed save.
 */
export async function extractPur({ creditorId, creditorName, awbNumber, file }) {
  const form = new FormData();
  form.append('creditor_id', String(creditorId));
  form.append('awb_number', String(awbNumber || ''));
  form.append('name', creditorName);
  form.append('pdf', file);

  let token = null;
  try { token = atob(atob(localStorage.getItem('token') || '')); } catch { token = null; }

  const res = await fetch(`${API_BASE_URL}/scraper/extract-pur`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });

  let body = {};
  try { body = await res.json(); } catch { /* a gateway error need not be JSON */ }

  // The server refuses a bill it has already read, which is a result, not a fault.
  if (res.status === 409) {
    return { ok: false, duplicate: true, message: body.message || 'This PUR is already saved' };
  }
  if (!res.ok || !body.success) {
    return { ok: false, duplicate: false, message: body.message || `Could not read this PUR (${res.status})` };
  }

  const d = body.data || {};
  return {
    ok: true,
    duplicate: false,
    extractionStatus: d.extractionStatus || d.extraction_status || null,
    invoiceNumber: d.invoiceNumber || d.invoice_number || null,
    lines: (d.lineItems || d.line_items || []).length,
    warnings: d.warnings || [],
    message: body.message,
  };
}

/* ── reading PURs back ────────────────────────────────────────────────────
   GET /api/scraper/pur?creditor_id=  -> { data: rows, total, page, limit }
   GET /api/scraper/pur/:id           -> { data: doc with lineItems[] }

   A row carries the document's own fields plus `lineItemCount` and the
   creditor it belongs to. On a line, the fields every brand shares are real
   columns and whatever else that brand's bill carries is kept verbatim in
   `data` — which is why the viewer builds its columns from the rows instead
   of assuming any one layout. */

/** Every bill read from one creditor, newest first. */
export async function listPurDocuments(creditorId) {
  const scope = creditorId ? `creditor_id=${encodeURIComponent(creditorId)}&` : '';
  const body = await readJson(await apiCall(`/scraper/pur?${scope}limit=200`, { method: 'GET' }));
  return (body.data || []).map((d) => ({
    ...d,
    // the server counts the lines under a name of its own
    lineCount: d.lineItemCount ?? d.line_item_count ?? (d.lineItems || []).length,
  }));
}

/** One bill, with its lines in order. */
export async function getPurDocument(id) {
  const body = await readJson(await apiCall(`/scraper/pur/${id}`, { method: 'GET' }));
  return body.data || null;
}

/* ── users ──────────────────────────────────────────────────────────────── */

/** GET /api/users/our-users — the customers a name can be matched to. */
export async function listUsers() {
  const body = await readJson(await apiCall('/users/our-users', { method: 'GET' }));
  return (body.data || [])
    // The customer's name comes back as `our_users` on this route; reading it
    // as `name` returned a list of blanks, so nothing ever matched.
    .map((u) => ({ id: u.id, name: u.our_users || u.custName || u.name || u.username || '' }))
    .filter((u) => u.id && u.name && u.name !== 'Unknown');
}

/* ── bank statement (steps 3-5) ──────────────────────────────────────────── */

/**
 * Read the workbook here, then hand the rows to the server.
 *
 * Only the reading stays in the browser — deciding who each name is, learning
 * the answer and flagging what could not be placed all happen server-side,
 * against every user and the names it has already been taught.
 *
 * The bank the statement came from is chosen before the file is and travels
 * with what is read. It stays on this screen for now: bank_details has no
 * column for it, and its create schema is strict, so sending it would be
 * refused. It joins the upload the day that column lands.
 */
export async function readStatementFile(file, bank = '') {
  if (!file) throw new Error('No statement to read');
  if (!bank) throw new Error('No bank chosen for this statement');
  return parseBankStatementFile(file, bank);
}

/** POST /api/invoices/bank-details/bulk — max 500 a call, so send in batches. */
export async function uploadBankRows(rows) {
  const BATCH = 500;
  let created = 0;
  const ambiguousNames = [];
  const placed = [];

  for (let i = 0; i < rows.length; i += BATCH) {
    const slice = rows.slice(i, i + BATCH)
      // `remark` is ours; the table has no column for it.
      .map(({ remark, ...row }) => row);
    const body = await readJson(await apiCall('/invoices/bank-details/bulk', {
      method: 'POST',
      body: JSON.stringify({ rows: slice }),
    }));
    created += body.data?.created ?? slice.length;
    if (Array.isArray(body.data?.ambiguousNames)) ambiguousNames.push(...body.data.ambiguousNames);
    if (Array.isArray(body.data?.rows)) placed.push(...body.data.rows);
  }
  return { created, ambiguousNames, placed };
}

/**
 * Put what just arrived onto each party's running balance.
 *
 * Only rows the server managed to place are counted — money it could not name
 * belongs to nobody yet. One call per side, which the server applies all or
 * none, rather than one call per party.
 */
export async function addArrivalsToBalances(placedRows) {
  const totals = { user: new Map(), creditor: new Map() };
  placedRows.forEach((r) => {
    const credit = Number(r.credit) || 0;
    const debit = Number(r.debit) || 0;
    const uid = r.userId ?? r.user_id;
    const cid = r.creditorId ?? r.creditor_id;
    if (credit > 0 && uid) totals.user.set(uid, (totals.user.get(uid) || 0) + credit);
    if (debit > 0 && cid) totals.creditor.set(cid, (totals.creditor.get(cid) || 0) + debit);
  });

  const send = async (kind, map) => {
    const updates = [...map.entries()]
      .map(([id, amount]) => ({
        [kind === 'creditor' ? 'creditor_id' : 'user_id']: id,
        operation: 'add',
        amount: Math.round(amount * 100) / 100,
      }))
      .filter((u) => u.amount >= 0.01);
    if (!updates.length) return 0;
    const path = kind === 'creditor' ? '/invoices/creditors/debit/bulk' : '/invoices/users/credit/bulk';
    await readJson(await apiCall(path, { method: 'PATCH', body: JSON.stringify({ updates }) }));
    return updates.length;
  };

  const [users, creditors] = await Promise.all([
    send('user', totals.user),
    send('creditor', totals.creditor),
  ]);
  return { users, creditors };
}

/**
 * GET /api/invoices/bank-details
 *
 * A row carries whoever the server resolved it to — a customer on the credit
 * side, a creditor on the debit side — or `miscellaneousStatus` when it could
 * not, with `matchCandidates` where several fitted.
 */
export async function listBankRows(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, v); });
  q.set('limit', params.limit || '1000');
  const body = await readJson(await apiCall(`/invoices/bank-details?${q}`, { method: 'GET' }));
  return (body.data || []).map((r) => ({
    id: r.id,
    name: r.name || '',
    date: r.date || '',
    credit: Number(r.credit) || 0,
    debit: Number(r.debit) || 0,
    userId: r.userId ?? r.user_id ?? null,
    userName: r.user?.Name || r.user?.name || '',
    creditorId: r.creditorId ?? r.creditor_id ?? null,
    creditorName: r.creditor?.name || r.creditor?.Name || '',
    unplaced: Boolean(r.miscellaneousStatus ?? r.miscellaneous_status),
    candidates: r.matchCandidates || r.match_candidates || null,
  }));
}

/**
 * PATCH /api/invoices/bank-details/bulk — point every row of a party at
 * somebody.
 *
 * Money in is owed by a customer and money out to a creditor, so the side
 * decides which link is sent. Either clears the unplaced flag and teaches the
 * server that this is one of the names that party's bank writes, so the next
 * statement places it on its own.
 */
export async function assignBankRows(ids, side, id) {
  if (!ids.length) return null;
  const link = side === 'debit' ? { creditor_id: id } : { user_id: id };
  const body = await readJson(await apiCall('/invoices/bank-details/bulk', {
    method: 'PATCH',
    body: JSON.stringify({ updates: ids.map((rowId) => ({ id: rowId, ...link })) }),
  }));
  return body.data;
}

/**
 * What a settlement leaves on the bank lines it was paid out of.
 *
 * The lines are spent oldest first, the same order the screen lists them in, so
 * the money goes against the earliest receipt rather than an arbitrary one.
 * Returns the `updates` a bulk PATCH takes: each line's remaining amount.
 */
export function planRowReduction(lines, side, applied) {
  let left = Math.round((Number(applied) || 0) * 100) / 100;
  const updates = [];
  for (const l of lines) {
    if (left <= 0.009) break;
    const have = Math.round((Number(side === 'credit' ? l.credit : l.debit) || 0) * 100) / 100;
    if (!l.id || have <= 0) continue;
    const take = Math.min(have, left);
    left = Math.round((left - take) * 100) / 100;
    updates.push({ id: l.id, [side]: Math.round((have - take) * 100) / 100 });
  }
  return updates;
}

/**
 * PATCH /api/invoices/bank-details/bulk — take the settled money off the lines.
 *
 * bank_details has no column saying how much of a line has been used, so what
 * is left of it is the amount itself: a line settled in full ends at zero and
 * drops off the screen. This is what makes the reduction outlast a reload —
 * holding it in the browser only lasted as long as the tab did.
 */
export async function reduceBankRows(updates) {
  if (!updates || !updates.length) return 0;
  await readJson(await apiCall('/invoices/bank-details/bulk', {
    method: 'PATCH',
    body: JSON.stringify({ updates }),
  }));
  return updates.length;
}

/* ── settlement (steps 6-7) ──────────────────────────────────────────────── */

/**
 * One invoice record, read the way the shipments table reads it.
 *
 * The shape varies: an air invoice carries its fields at the top level, a sea
 * one nests them under `invoice`, the numbers live in `invoiceData` which is
 * sometimes a JSON string, and a paid invoice may leave partial_payment at 0.
 */
const readInvoice = (record) => {
  const raw = Array.isArray(record) ? record[0] : record;
  if (!raw) return null;
  const inv = raw.invoice || raw;
  const summary = inv.summary || inv;

  const amount = Number(summary.grand_total ?? summary.grandTotal ?? 0) || 0;
  const status = String(raw.status ?? inv.status ?? 'unpaid').toLowerCase();
  const partial = Number(
    raw.partialPayment ?? raw.partial_payment ?? inv.partialPayment ?? inv.partial_payment ?? 0,
  ) || 0;
  const alreadyPaid = status === 'paid' ? Math.max(partial, amount) : partial;

  const entries = [raw.invoiceData, raw.invoice_data, inv.invoiceData, inv.invoice_data]
    .flatMap((src) => {
      let parsed = src;
      if (typeof src === 'string') { try { parsed = JSON.parse(src); } catch { parsed = []; } }
      return Array.isArray(parsed) ? parsed : [];
    });
  const number = entries.map((e) => String(e?.invoiceNumber || e?.invoice_number || '').trim())
    .find(Boolean) || `Invoice #${raw.id ?? inv.id ?? ''}`.trim();

  const date = raw.invoiceDate ?? raw.invoice_date ?? inv.invoiceDate ?? inv.invoice_date
    ?? entries[0]?.invoiceDate ?? entries[0]?.invoice_date ?? '';

  return { id: raw.id ?? inv.id, number, date: String(date).slice(0, 10), amount, alreadyPaid, status };
};

/**
 * What a receipt can be put against: that customer's invoices with something
 * still owing. Air and sea are kept on separate routes, so both are asked and
 * whichever answers is used.
 */
export async function listUserInvoices(userId) {
  const fetchOne = async (path) => {
    try {
      const body = await readJson(await apiCall(path, { method: 'GET' }));
      return body.data || [];
    } catch { return []; }
  };
  const [air, sea] = await Promise.all([
    fetchOne(`/invoices/user/${encodeURIComponent(userId)}`),
    fetchOne(`/invoices/sea/user/${encodeURIComponent(userId)}`),
  ]);
  return [...air, ...sea]
    .map(readInvoice)
    .filter((b) => b && b.id && b.amount > 0)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));   // oldest first
}

export async function listOpenInvoices(userId) {
  const all = await listUserInvoices(userId);
  return all.filter((b) => b.amount - b.alreadyPaid > 0.009);
}

/**
 * Put the entries in date order and run a balance down them.
 *
 * The balance after every line is the whole point of a ledger: it says what
 * was still owed on the day, not just at the end. A customer owes us what we
 * billed less what they paid; for a creditor the two sides swap, so the
 * running total is worked out the other way round.
 */
const ledgerOf = (entries, kind) => {
  const sorted = [...entries].sort((a, b) => (
    String(a.date).localeCompare(String(b.date))
    // a payment on the same day as a bill comes after it: the bill is what it pays
    || (a.debit > 0 ? 0 : 1) - (b.debit > 0 ? 0 : 1)
  ));

  let running = 0;
  const withBalance = sorted.map((e) => {
    running = Math.round((running + (kind === 'creditor'
      ? (e.credit || 0) - (e.debit || 0)
      : (e.debit || 0) - (e.credit || 0))) * 100) / 100;
    return { ...e, balance: running };
  });

  const sum = (k) => Math.round(sorted.reduce((t, e) => t + (e[k] || 0), 0) * 100) / 100;
  return { entries: withBalance, totalDebit: sum('debit'), totalCredit: sum('credit'), closing: running };
};

/**
 * The payments this party made, one line per bill they went against.
 *
 * Also adds up what each bill has been paid, which is the only record of it on
 * the creditor side — a PUR document holds no paid amount of its own.
 */
const settledLines = async (kind, partyId, numberOf) => {
  const records = await listPaymentRecords(kind === 'creditor' ? { creditorId: partyId } : { userId: partyId })
    .catch(() => []);
  const lines = [];
  const paidPer = new Map();
  records.forEach((r) => {
    r.items.forEach((i) => {
      if (i.status !== 'active') return;       // taken back; it never happened
      paidPer.set(i.documentId, Math.round(((paidPer.get(i.documentId) || 0) + i.amount) * 100) / 100);
      lines.push({
        date: String(r.at).slice(0, 10),
        number: numberOf.get(i.documentId) || `#${i.documentId}`,
        // the receipt it was part of rides with the words, so several bills
        // cleared by one of them read as one payment
        note: `${kind === 'creditor' ? 'Paid to them' : 'Payment received'} #${r.id}`,
        ...(kind === 'creditor' ? { debit: i.amount, credit: 0 } : { debit: 0, credit: i.amount }),
      });
    });
  });
  return { lines, paidPer };
};

/** `Paid`, or what is still owed on this bill and how much of it. */
const billState = (total, paid) => {
  const due = Math.round((total - paid) * 100) / 100;
  if (due <= 0.009) return { tag: 'Paid', due: 0 };
  if (paid > 0.009) return { tag: `Part paid · ₹${money(due)} due`, due };
  return { tag: 'Unpaid', due };
};

/** What arrived on the statement and has not been put against a bill yet. */
const unappliedOf = async (kind, partyId) => {
  const rows = await listBankRows(kind === 'creditor'
    ? { creditor_id: partyId, type: 'debit' }
    : { user_id: partyId, type: 'credit' }).catch(() => []);
  return Math.round(rows.reduce((t, r) => t + (kind === 'creditor' ? r.debit : r.credit), 0) * 100) / 100;
};

/**
 * A customer's account, both sides of it.
 *
 * What we billed them is a debit — it is what they owe us — and what they paid
 * against those bills is a credit. The payments come from the payment records
 * rather than the statement rows: a record says which invoice the money went
 * against and on what day, where a statement row only says money arrived, and
 * carries what is left of it rather than what came in.
 */
export async function userLedger(userId) {
  const invoices = await listUserInvoices(userId);
  const numberOf = new Map(invoices.map((i) => [i.id, i.number]));
  const [{ lines, paidPer }, unapplied] = await Promise.all([
    settledLines('customer', userId, numberOf),
    unappliedOf('customer', userId),
  ]);

  const bills = invoices.map((i) => {
    // partial_payment carries what the invoice itself knows it has been paid,
    // which includes anything settled before payments were ever recorded.
    const paid = Math.max(i.alreadyPaid || 0, paidPer.get(i.id) || 0);
    const { tag, due } = billState(i.amount, paid);
    return { date: i.date, number: i.number, note: 'Invoice', debit: i.amount, credit: 0, tag, due };
  });

  /* A payment line carries the standing of the bill it paid, so the column
     reads as a status the whole way down rather than changing its meaning on
     every other row. */
  const standing = new Map(bills.map((b) => [b.number, b]));
  const paidLines = lines.map((l) => {
    const bill = standing.get(l.number);
    return { ...l, tag: bill?.tag || '', due: bill?.due || 0 };
  });

  return {
    ...ledgerOf([...bills, ...paidLines], 'customer'),
    unapplied,
    open: bills.filter((b) => b.due > 0.009),
  };
}

/**
 * A creditor's account, which runs the other way.
 *
 * Their purchase bill is a credit — it is what we owe them — and what we have
 * paid against it is a debit.
 */
export async function creditorLedger(creditorId) {
  const bills = await listPurDocuments(creditorId).catch(() => []);
  const numberOf = new Map(bills.map((d) => [d.id, d.invoiceNumber || `#${d.id}`]));
  const [{ lines, paidPer }, unapplied] = await Promise.all([
    settledLines('creditor', creditorId, numberOf),
    unappliedOf('creditor', creditorId),
  ]);

  const theirs = bills
    .map((d) => {
      const total = Number(d.totals?.grandTotal ?? d.totals?.grand_total ?? 0) || 0;
      const { tag, due } = billState(total, paidPer.get(d.id) || 0);
      return {
        date: d.invoiceDate || '',
        number: d.invoiceNumber || '',
        note: 'Purchase bill',
        debit: 0,
        credit: total,
        tag,
        due,
      };
    })
    .filter((e) => e.credit > 0);

  const standing = new Map(theirs.map((b) => [b.number, b]));
  const paidLines = lines.map((l) => {
    const bill = standing.get(l.number);
    return { ...l, tag: bill?.tag || '', due: bill?.due || 0 };
  });

  return {
    ...ledgerOf([...theirs, ...paidLines], 'creditor'),
    unapplied,
    open: theirs.filter((b) => b.due > 0.009),
  };
}

/**
 * What a payment out can be put against: the bills that creditor has sent us
 * and we have not cleared.
 *
 * A PUR records whether it is paid but not how much of it has been, so a
 * partly-paid bill cannot say what is left — `alreadyPaid` stays 0 until the
 * server carries that figure.
 */
export async function listOpenPurBills(creditorId) {
  const body = await readJson(await apiCall(
    `/scraper/pur?creditor_id=${encodeURIComponent(creditorId)}&limit=200`, { method: 'GET' },
  ));
  return (body.data || [])
    .filter((d) => d.status !== 'paid')
    .map((d) => ({
      id: d.id,
      number: d.invoiceNumber || '(no number)',
      date: d.invoiceDate || '',
      amount: Number(d.totals?.grandTotal ?? d.totals?.grand_total ?? 0),
      alreadyPaid: 0,
      note: [d.brand, d.status === 'partially_paid' ? 'partly paid' : null].filter(Boolean).join(' · '),
    }))
    .filter((b) => b.amount > 0);
}

/**
 * Put the money against the bills.
 *
 * Two calls, in this order:
 *
 *  1. The party's balance comes down by what is being applied, and says which
 *     documents it paid (`applied_to`) and which bank line it came from. The
 *     server checks in one transaction that every document belongs to them and
 *     that the balance covers it, and saves a payment record that can be undone
 *     later. Nothing is marked paid if this fails, which is why it goes first:
 *     the old order marked the bills and then found the money was not there.
 *  2. Each document's status, which the balance route deliberately leaves to us.
 *
 * Step 2 is not atomic. If one call fails, the ones before it have been applied,
 * so the failure names them along with the payment record that can undo the lot.
 *
 * An invoice takes the amount — `partial_payment` is an increment the service
 * adds to what is already there. A PUR takes only the new status, because
 * pur_documents has no paid amount of its own; its paid total is the sum of the
 * payment items that now exist for it.
 */
export async function settle({ side, allocations, received, partyId, bankRowId }) {
  const applied = Math.round(allocations.reduce((t, a) => t + a.amount, 0) * 100) / 100;

  /* The balance holds the money of theirs we are still sitting on: a statement
     arriving puts it up, and what a settlement uses takes it down. */
  let record = null;
  if (partyId && applied >= 0.01) {
    const out = await adjustBalance(side === 'debit' ? 'creditor' : 'customer', partyId, -applied, {
      appliedTo: allocations.map((a) => ({ document_id: a.targetId, amount: a.amount })),
      bankRowId,
    });
    record = out?.paymentRecord || null;
  }

  const done = [];
  for (const a of allocations) {
    const label = a.number || a.targetId;
    try {
      if (side === 'credit') {
        await readJson(await apiCall(`/invoices/${a.targetId}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ status: a.status, partial_payment: a.amount }),
        }));
      } else {
        await readJson(await apiCall(`/scraper/pur/${a.targetId}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ status: a.status }),
        }));
      }
      done.push(label);
    } catch (err) {
      const prefix = done.length ? `${done.join(', ')} went through, then ` : '';
      const undo = record ? ` The money has already moved — payment #${record.id} undoes it.` : '';
      throw new Error(`${prefix}${label} failed — ${err.message}.${undo}`);
    }
  }

  return {
    ok: true,
    settled: done.length,
    applied,
    balanceMoved: Boolean(record) || !partyId,
    paymentRecord: record,
    amountRecorded: side === 'credit',
  };
}

/**
 * Move a party's running balance.
 *
 * `users.credit` and `creditors_details.debit` hold the money of theirs we are
 * sitting on — received but not yet put against a bill. The call is a delta, so
 * two settlements at once cannot overwrite each other, and the server refuses
 * to take a balance below zero.
 */
export async function adjustBalance(kind, id, delta, { appliedTo, bankRowId } = {}) {
  const amount = Math.round(Math.abs(delta) * 100) / 100;
  if (!id || amount < 0.01) return null;
  const path = kind === 'creditor'
    ? `/invoices/creditors/${id}/debit`
    : `/invoices/users/${id}/credit`;
  /* A subtract may say what it paid, and only a subtract may: the server reads
     applied_to as the documents this money cleared, checks they are the party's
     own and add up to the amount, and records the payment so it can be undone.
     bank_detail_id names the line it came from and needs applied_to with it. */
  const spend = delta < 0 && appliedTo?.length;
  const body = await readJson(await apiCall(path, {
    method: 'PATCH',
    body: JSON.stringify({
      operation: delta >= 0 ? 'add' : 'subtract',
      amount,
      ...(spend ? { applied_to: appliedTo } : {}),
      ...(spend && bankRowId ? { bank_detail_id: bankRowId } : {}),
    }),
  }));
  const data = body.data;
  return Array.isArray(data) ? data[0] : data;
}

/* ── the statement file itself ───────────────────────────────────────────── */

/**
 * Keep the workbook, not just the rows read out of it.
 *
 * Three steps, which is how the server wants it: ask for a link, PUT the file
 * straight to storage (so it never passes through the API), then register the
 * key against the bank. The PUT is a plain fetch with no Authorization header
 * — the link already carries its own signature, and the Content-Type must be
 * exactly the one handed back or the signature does not match.
 */
export async function archiveStatementFile(file, bankName) {
  const asked = await readJson(await apiCall('/invoices/bank-statements/upload-url', {
    method: 'POST',
    body: JSON.stringify({ bank_name: bankName, file_name: file.name }),
  }));
  const { uploadUrl, key, contentType } = asked.data || {};
  if (!uploadUrl || !key) throw new Error('No upload link came back for this statement');

  const put = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType || file.type || 'application/octet-stream' },
    body: file,
  });
  if (!put.ok) throw new Error(`The file did not reach storage (${put.status})`);

  const saved = await readJson(await apiCall('/invoices/bank-statements', {
    method: 'POST',
    body: JSON.stringify({ bank_name: bankName, s3_key: key, original_file_name: file.name }),
  }));
  return saved.data;
}

/** GET /api/invoices/bank-statements — newest first. */
export async function listBankStatements(params = {}) {
  const q = new URLSearchParams();
  if (params.bankName) q.set('bank_name', params.bankName);
  q.set('limit', params.limit || '25');
  const body = await readJson(await apiCall(`/invoices/bank-statements?${q}`, { method: 'GET' }));
  return (body.data || []).map((r) => ({
    id: r.id,
    bank: r.bankName || r.bank_name || '',
    fileName: r.originalFileName || r.original_file_name || '',
    at: r.createdAt || r.created_at || '',
  }));
}

/** A five-minute link to the stored workbook. */
export async function statementDownloadUrl(id) {
  const body = await readJson(await apiCall(`/invoices/bank-statements/${id}/download-url`, { method: 'GET' }));
  return body.data?.downloadUrl || body.data?.url || body.downloadUrl || '';
}

/* ── payments, and taking one back ───────────────────────────────────────── */

/**
 * GET /api/invoices/payment-records
 *
 * One record per settlement, newest first, with an item per document it paid.
 * An item carries its own status, so a record is 'active' until some of it is
 * reversed ('partially_reversed') and 'reversed' once all of it is.
 */
export async function listPaymentRecords(params = {}) {
  const q = new URLSearchParams();
  const send = { user_id: params.userId, creditor_id: params.creditorId, invoice_id: params.invoiceId, status: params.status };
  Object.entries(send).forEach(([k, v]) => { if (v !== undefined && v !== '' && v !== null) q.set(k, v); });
  q.set('limit', params.limit || '25');
  const body = await readJson(await apiCall(`/invoices/payment-records?${q}`, { method: 'GET' }));
  return (body.data || []).map((r) => ({
    id: r.id,
    side: r.partyType === 'creditor' ? 'debit' : 'credit',
    partyId: r.userId ?? r.creditorId ?? null,
    partyName: r.user?.name || r.creditor?.name || '',
    amount: Number(r.amount) || 0,
    live: Number(r.amountActive) || 0,
    takenBack: Number(r.amountReversed) || 0,
    status: r.status,
    bankRowId: r.bankDetailId ?? null,
    at: r.createdAt || '',
    items: (r.items || []).map((i) => ({
      id: i.id,
      documentId: i.invoiceId ?? i.purDocumentId ?? null,
      amount: Number(i.amount) || 0,
      status: i.status,
      reason: i.reversalReason || '',
    })),
  }));
}

/** POST …/payment-records/:id/undo — the whole payment goes back. */
export async function undoPayment(id, reason) {
  const body = await readJson(await apiCall(`/invoices/payment-records/${id}/undo`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }));
  return body.data;
}

/**
 * POST …/payment-records/items/undo/bulk — several documents' shares at once.
 *
 * All or nothing, so a batch either goes back whole or not at all: the server
 * refuses the lot if one item is already reversed or a bill no longer holds
 * that much paid.
 */
export async function undoPaymentItems(ids, reason) {
  if (!ids.length) return null;
  if (ids.length === 1) return undoPaymentItem(ids[0], reason);
  const body = await readJson(await apiCall('/invoices/payment-records/items/undo/bulk', {
    method: 'POST',
    body: JSON.stringify({ ids, reason }),
  }));
  return body.data;
}

/** POST …/payment-records/items/:id/undo — one document's share of it. */
export async function undoPaymentItem(id, reason) {
  const body = await readJson(await apiCall(`/invoices/payment-records/items/${id}/undo`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }));
  return body.data;
}

/**
 * Put money back on the bank line a payment came off.
 *
 * The server's undo returns the money to the party's balance and takes it off
 * the document, but the line on this screen carries what is left of it, and
 * that is ours to keep straight — see `reduceBankRows`, which took it off.
 */
export async function restoreBankRow(rowId, side, amount) {
  if (!rowId || !(amount > 0)) return false;
  const body = await readJson(await apiCall(`/invoices/bank-details/${rowId}`, { method: 'GET' }));
  const row = body.data || {};
  const have = Number(side === 'credit' ? row.credit : row.debit) || 0;
  await reduceBankRows([{ id: rowId, [side]: Math.round((have + amount) * 100) / 100 }]);
  return true;
}

/**
 * What this party has already been paid, as lines that can be taken back.
 *
 * A payment record holds the documents one settlement cleared; this reads them
 * back one document at a time, with the bill's own number beside it, so the
 * settle screen can show what was paid under what is still open. Only items
 * that are still active are returned — a reversed one has nothing left to undo.
 */
export async function listSettledBills({ side, partyId }) {
  if (!partyId) return [];
  const [records, docs] = await Promise.all([
    listPaymentRecords(side === 'debit' ? { creditorId: partyId } : { userId: partyId }),
    side === 'debit' ? listPurDocuments(partyId) : listUserInvoices(partyId),
  ]);
  const numberOf = new Map(docs.map((d) => [d.id, d.number || d.invoiceNumber || '']));

  const out = [];
  records.forEach((r) => {
    r.items.forEach((i) => {
      if (i.status !== 'active') return;
      out.push({
        id: i.id,                         // the payment item, which is what an undo takes
        paymentId: r.id,
        documentId: i.documentId,
        number: numberOf.get(i.documentId) || `#${i.documentId}`,
        amount: i.amount,
        at: r.at,
        bankRowId: r.bankRowId,
      });
    });
  });
  return out;
}

/* ── allocation ──────────────────────────────────────────────────────────── */

/**
 * Spread `available` across `picked` bills in the order given.
 *
 * Each bill takes what it still owes until the money runs out; the one the money
 * runs out inside takes the remainder and stays partly due. This is the rule the
 * spec describes with ₹1,00,000 against ₹1,06,000, and the screen shows its
 * result before anything is submitted.
 */
export function allocate(available, picked) {
  let left = Number(available) || 0;
  return picked.map((bill) => {
    const owed = (Number(bill.amount) || 0) - (Number(bill.alreadyPaid) || 0);
    const applied = Math.max(0, Math.min(left, owed));
    left = Math.round((left - applied) * 100) / 100;
    const stillDue = Math.round((owed - applied) * 100) / 100;
    return {
      ...bill,
      owed,
      applied,
      stillDue,
      outcome: applied <= 0 ? 'untouched' : stillDue > 0 ? 'partially_paid' : 'paid',
    };
  });
}
